import React from 'react';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, CartesianGrid, ReferenceDot } from 'recharts';

var API = 'https://api.algrok.com';

function fmtRs(n){if(n==null||isNaN(n))return'--';return'\u20B9'+Number(n).toFixed(1);}

function StraddleTrackerM(){
  var [state,setState]=useState({});
  var [history,setHistory]=useState([]);
  var [events,setEvents]=useState([]);
  var [pnlPairs,setPnlPairs]=useState([]);
  var [showSettings,setShowSettings]=useState(false);
  var pollRef=useRef(null);

  var fetchAll=useCallback(function(){
    fetch(API+'/straddle/state').then(function(r){return r.json();}).then(function(d){
      if(d && !d.error) setState(d);
    }).catch(function(){});
    fetch(API+'/straddle/history').then(function(r){return r.json();}).then(function(d){
      if(d && d.history) setHistory(d.history);
    }).catch(function(){});
    fetch(API+'/straddle/events').then(function(r){return r.json();}).then(function(d){
      if(d && d.events) setEvents(d.events);
    }).catch(function(){});
    fetch(API+'/straddle/pnl').then(function(r){return r.json();}).then(function(d){
      if(!d) return;
      if(d.pnl_pairs) setPnlPairs(d.pnl_pairs);
    }).catch(function(){});
  },[]);

  useEffect(function(){
    fetchAll();
    pollRef.current=setInterval(fetchAll,10000);
    return function(){if(pollRef.current)clearInterval(pollRef.current);};
  },[fetchAll]);

  var cfg=state.config||{};
  var setCfg=function(key,val){
    var payload={};payload[key]=val;
    fetch(API+'/straddle/config',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)})
      .then(function(){fetchAll();}).catch(function(){});
  };

  var chartData=useMemo(function(){return history.map(function(h){return {time:h.time,combined:h.combined,vwap:h.vwap,is_roll:h.is_roll};});},[history]);

  var eventDots=useMemo(function(){
    var dots=[];
    var times=history.map(function(h){return h.time;});
    events.forEach(function(ev){
      var idx=times.indexOf(ev.time);
      if(idx>=0&&ev.type!=='roll'){
        dots.push({x:ev.time,y:ev.combined||history[idx].combined,type:ev.type});
      }
    });
    return dots;
  },[events,history]);

  var sideColor=state.side==='in'?'#22c55e':state.side==='out'?'#ef4444':'#9ca3af';
  var posStatus=state.position_status||'WAITING';
  var posColor=posStatus==='OPEN'?'#22c55e':'#9ca3af';
  var dayPnl=state.day_pnl||0;
  var pnlColor=dayPnl>0?'#22c55e':dayPnl<0?'#ef4444':'#9ca3af';
  var dayPnlTotal=state.day_pnl_total||0;
  var dayPnlTotalColor=dayPnlTotal>0?'#22c55e':dayPnlTotal<0?'#ef4444':'#9ca3af';
  var disabledToday=!!state.trade_disabled_today;

  // Live trading was removed from straddle_tracker.py on 16-Aug-2026 — this
  // module has no order path at all. exit_all_manual still stops tracking
  // for the day, so the control was relabelled rather than deleted.
  var doStopToday=function(){
    if(!window.confirm('Stop tracking for today?')) return;
    fetch(API+'/straddle/exit-all',{method:'POST'})
      .then(function(r){return r.json();}).then(function(d){
        if(!(d&&d.ok)) alert('Failed: '+(d&&d.error?d.error:'unknown'));
        fetchAll();
      }).catch(function(e){alert('Error: '+e.message);});
  };

  return (
    <div style={{padding:10,color:'#e5e7eb'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8,gap:6}}>
        <span style={{fontSize:14,fontWeight:800}}>📈 Straddle</span>
        {disabledToday&&<span style={{fontSize:8,padding:'2px 6px',background:'rgba(156,163,175,0.18)',color:'#9ca3af',borderRadius:3,fontWeight:800}}>STOPPED TODAY</span>}
        {!disabledToday&&<button onClick={doStopToday} style={{background:'rgba(156,163,175,0.15)',color:'#9ca3af',border:'1px solid rgba(156,163,175,0.3)',padding:'4px 8px',borderRadius:4,fontSize:9,fontWeight:800}}>Stop today</button>}
      </div>

      {/* Live Position bar (mobile compact) */}

      {/* Headline 4x2 grid */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:6,marginBottom:8}}>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'5px 7px'}}>
          <div style={{fontSize:8,color:'#6b7280'}}>ATM</div>
          <div style={{fontSize:12,fontWeight:700}}>{state.atm_strike||'--'}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'5px 7px'}}>
          <div style={{fontSize:8,color:'#6b7280'}}>Spot</div>
          <div style={{fontSize:12,fontWeight:700}}>{state.spot?Number(state.spot).toFixed(0):'--'}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'5px 7px'}}>
          <div style={{fontSize:8,color:'#6b7280'}}>Combined</div>
          <div style={{fontSize:11,fontWeight:700,color:'#22d3ee'}}>{fmtRs(state.combined)}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'5px 7px'}}>
          <div style={{fontSize:8,color:'#6b7280'}}>VWAP</div>
          <div style={{fontSize:11,fontWeight:700,color:'#a78bfa'}}>{fmtRs(state.vwap)}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'5px 7px'}}>
          <div style={{fontSize:8,color:'#6b7280'}}>Side</div>
          <div style={{fontSize:10,fontWeight:700,color:sideColor}}>{state.side?state.side.toUpperCase():'--'}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'5px 7px'}}>
          <div style={{fontSize:8,color:'#6b7280'}}>Pos</div>
          <div style={{fontSize:10,fontWeight:700,color:posColor}}>{posStatus==='OPEN'?'🟢':'⚪'} {posStatus}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'5px 7px'}}>
          <div style={{fontSize:8,color:'#6b7280'}}>Day P/L</div>
          <div style={{fontSize:11,fontWeight:700,color:pnlColor}}>{dayPnl>=0?'+':''}{dayPnl.toFixed(1)}</div>
        </div>
        <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,padding:'5px 7px'}}>
          <div style={{fontSize:8,color:'#6b7280'}}>Today</div>
          <div style={{fontSize:9,fontWeight:700}}>{state.rolls_today||0}r·{state.entries_today||0}↓·{state.exits_today||0}↑</div>
        </div>
      </div>

      {/* Settings toggle */}
      <button onClick={function(){setShowSettings(!showSettings);}} style={{width:'100%',padding:'6px',fontSize:11,fontWeight:700,background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,color:'#9ca3af',marginBottom:8}}>
        {showSettings?'Hide Settings':'Show Settings'}
      </button>

      {showSettings&&<div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:10,marginBottom:8}}>
        <div style={{fontSize:10,color:'#9ca3af',fontWeight:600,marginBottom:4}}>VWAP Type</div>
        <select value={cfg.vwap_type||'combined_vwap'} onChange={function(e){setCfg('vwap_type',e.target.value);}} style={{width:'100%',background:'#0a0f1c',border:'1px solid #1f2937',color:'#e5e7eb',padding:'5px 7px',borderRadius:4,fontSize:11,marginBottom:8}}>
          <option value="combined_vwap">Combined Premium VWAP</option>
          <option value="spot_vwap">NIFTY Spot VWAP</option>
          <option value="simple_ma">Simple MA</option>
          <option value="anchored_session_start">Anchored — Session Start</option>
          <option value="anchored_last_signal">Anchored — Last Signal</option>
          <option value="anchored_extreme">Anchored — Session Extreme</option>
          <option value="anchored_user">Anchored — User Click</option>
        </select>
        <div style={{fontSize:10,color:'#9ca3af',fontWeight:600,marginBottom:4}}>Roll Trigger</div>
        <select value={cfg.roll_trigger||'past_50pts'} onChange={function(e){setCfg('roll_trigger',e.target.value);}} style={{width:'100%',background:'#0a0f1c',border:'1px solid #1f2937',color:'#e5e7eb',padding:'5px 7px',borderRadius:4,fontSize:11}}>
          <option value="past_50pts">Past 50pts (default)</option>
          <option value="immediate">Immediate</option>
          <option value="pure_anchored_vwap_sl">Pure VWAP SL (no spot trigger)</option>
        </select>
        {cfg.vwap_type==='simple_ma'&&<>
          <div style={{fontSize:10,color:'#9ca3af',fontWeight:600,marginTop:8,marginBottom:4}}>MA Window</div>
          <input type="number" min={5} max={360} value={cfg.ma_window||30} onChange={function(e){setCfg('ma_window',+e.target.value);}} style={{width:'100%',background:'#0a0f1c',border:'1px solid #1f2937',color:'#e5e7eb',padding:'5px 7px',borderRadius:4,fontSize:11}}/>
        </>}
        {cfg.vwap_type==='anchored_extreme'&&<>
          <div style={{fontSize:10,color:'#9ca3af',fontWeight:600,marginTop:8,marginBottom:4}}>Extreme Dead-band (%)</div>
          <input type="number" min={0.5} max={50} step={0.5} value={cfg.extreme_deadband_pct||5.0} onChange={function(e){setCfg('extreme_deadband_pct',+e.target.value);}} style={{width:'100%',background:'#0a0f1c',border:'1px solid #1f2937',color:'#e5e7eb',padding:'5px 7px',borderRadius:4,fontSize:11}}/>
        </>}
        {cfg.vwap_type==='anchored_user'&&<button onClick={function(){fetch(API+'/straddle/anchor-now',{method:'POST'}).then(function(){fetchAll();}).catch(function(){});}} style={{marginTop:8,width:'100%',background:'#1e3a8a',color:'#dbeafe',border:'1px solid #3b82f6',padding:'6px',borderRadius:4,fontSize:11,fontWeight:700}}>📍 Anchor Now</button>}
        {state.anchor_time&&cfg.vwap_type&&cfg.vwap_type.indexOf('anchored_')===0&&<div style={{marginTop:8,padding:'5px 7px',background:'rgba(59,130,246,0.08)',border:'1px solid rgba(59,130,246,0.25)',borderRadius:4,fontSize:9}}>
          <span style={{color:'#93c5fd',fontWeight:700}}>📍 {state.anchor_time}</span> <span style={{color:'#9ca3af'}}>@ ₹{state.anchor_combined}</span>
        </div>}

      </div>}

      {/* Chart */}
      <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:6,height:260,marginBottom:8}}>
        {chartData.length===0?
          <div style={{display:'flex',height:'100%',alignItems:'center',justifyContent:'center',color:'#6b7280',fontSize:11}}>
            Waiting for live data
          </div>
        :
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{top:6,right:6,left:0,bottom:16}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937"/>
              <XAxis dataKey="time" stroke="#9ca3af" fontSize={8} interval="preserveStartEnd" minTickGap={40}/>
              <YAxis stroke="#9ca3af" fontSize={8} tickFormatter={function(v){return '\u20B9'+v.toFixed(0);}}/>
              <Tooltip contentStyle={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:6,fontSize:10}} formatter={function(v){return [fmtRs(v),''];}}/>
              <Line type="monotone" dataKey="combined" stroke="#22d3ee" strokeWidth={1.5} dot={false} isAnimationActive={false}/>
              <Line type="monotone" dataKey="vwap" stroke="#a78bfa" strokeWidth={1.2} strokeDasharray="4 3" dot={false} isAnimationActive={false}/>
              {chartData.filter(function(d){return d.is_roll;}).map(function(d,i){
                return <ReferenceLine key={'r'+i} x={d.time} stroke="#fb923c" strokeDasharray="2 2"/>;
              })}
              {events.filter(function(ev){return ev.type==='anchor';}).map(function(ev,i){
                return <ReferenceLine key={'a'+i} x={ev.time} stroke="#3b82f6" strokeDasharray="3 4"/>;
              })}
              {eventDots.map(function(d,i){
                var color=d.type==='entry'?'#22c55e':d.type==='exit'?'#ef4444':d.type==='anchor'?'#3b82f6':'#9ca3af';
                return <ReferenceDot key={'e'+i} x={d.x} y={d.y} r={3} fill={color} stroke="#fff" strokeWidth={1}/>;
              })}
            </LineChart>
          </ResponsiveContainer>
        }
      </div>

      {/* Event log */}
      <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:10}}>
        <div style={{fontSize:11,fontWeight:700,marginBottom:6}}>📋 Recent Events</div>
        {events.length===0?
          <div style={{fontSize:10,color:'#6b7280'}}>No events yet.</div>
        :
          <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:200,overflowY:'auto'}}>
            {events.slice().reverse().slice(0,30).map(function(ev,i){
              var color=ev.type==='roll'?'#fb923c':ev.type==='entry'?'#22c55e':ev.type==='exit'?'#ef4444':ev.type==='anchor'?'#3b82f6':'#9ca3af';
              var icon=ev.type==='roll'?'🔄':ev.type==='entry'?'⬇️':ev.type==='exit'?'⬆️':ev.type==='anchor'?'📍':'•';
              return <div key={i} style={{borderLeft:'2px solid '+color,paddingLeft:6,fontSize:9}}>
                <div style={{display:'flex',gap:4}}><span style={{color:color,fontWeight:700}}>{ev.time}</span><span style={{color:color,fontWeight:700,textTransform:'uppercase'}}>{icon} {ev.type}</span></div>
                {ev.type==='roll'?
                  <div style={{color:'#9ca3af',marginTop:1}}>ATM {ev.atm_strike} · spot {Number(ev.spot).toFixed(0)}</div>
                :ev.type==='anchor'?
                  <div style={{color:'#9ca3af',marginTop:1}}>Anchor @ ₹{ev.combined}</div>
                :
                  <div style={{color:'#9ca3af',marginTop:1}}>₹{ev.combined} vs VWAP ₹{ev.vwap}</div>
                }
              </div>;
            })}
          </div>
        }
      </div>


      {/* P/L Panel */}
      <div style={{background:'#0a0f1c',border:'1px solid #1f2937',borderRadius:8,padding:10,marginTop:8}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6,paddingBottom:4,borderBottom:'1px solid #1f2937'}}>
          <span style={{fontSize:11,fontWeight:700}}>💰 P/L (per lot)</span>
          <span style={{fontSize:10,color:'#9ca3af'}}>Day: <span style={{color:pnlColor,fontWeight:700}}>₹{dayPnl>=0?'+':''}{dayPnl.toFixed(1)}</span> ({pnlPairs.length})</span>
        </div>
        {pnlPairs.length===0?
          <div style={{fontSize:10,color:'#6b7280'}}>No closed trades yet.</div>
        :
          <div style={{maxHeight:180,overflowY:'auto'}}>
            {pnlPairs.slice().reverse().map(function(p,i){
              var pnlC=p.pnl>0?'#22c55e':p.pnl<0?'#ef4444':'#9ca3af';
              var rsnC=p.exit_reason==='vwap'?'#a78bfa':'#fb923c';
              return <div key={i} style={{display:'grid',gridTemplateColumns:'auto 1fr auto auto',gap:6,padding:'4px 0',fontSize:9,borderBottom:'1px solid rgba(31,41,55,0.4)'}}>
                <span style={{color:'#6b7280'}}>{pnlPairs.length-i}</span>
                <span style={{color:'#9ca3af'}}>{p.entry_time}→{p.exit_time} {p.strike}</span>
                <span style={{color:rsnC,fontSize:8,fontWeight:700,textTransform:'uppercase'}}>{p.exit_reason}</span>
                <span style={{color:pnlC,fontWeight:700}}>{p.pnl>0?'+':''}{p.pnl.toFixed(1)}</span>
              </div>;
            })}
          </div>
        }
      </div>
    </div>
  );
}

export default StraddleTrackerM;
